import React from 'react';

// Typescript feature: "Nominal types".
// This is a string in structure but it's treated as a distinct type.
export type Uid = number & { __brand: 'UID' };


// This is used as a base class for records that we wish to maintain an undo history for.
// For undo/redo history, we need to be manipulating objects that
// specify a unique identifier as an "id" attribute, at the very least.
export interface ObjectWithUid {
  // A unique identifier.
  id: Uid;
}


// Just a small helper function used to to concatenate CSS class names
export function truthyJoin(...names:(string|null|false|undefined)[]): string {
  return names.filter((name) => (name !== undefined) && (name !== null) && (name !== false) && (name.length > 0)).join(" "); 
}


// Number parsing from a string to a float, also returning the precision for later rounding.
// Why does this exist?  This:  https://aqandrew.com/blog/0.30000000000000004/
export function parseAndGetPrecision(numberString: string, defaultString: string): [number, number] {
  var stringValue = numberString.trim();
  var value = parseFloat(stringValue);
  if (isNaN(value)) {
    stringValue = defaultString.trim();
    value = parseFloat(stringValue);
    // If even the default is broken, give up.
    if (isNaN(value)) {
      return [NaN, 0];
    }
  }
  const parts = stringValue.split(".");
  const precision = parts.length > 1 ? parts[1].length : 0;
  return [value, precision];
}


// This is a sorting function that will sort strings while respecting
// the relative value of numbers inlined into the strings.
// For example, it would sort the following strings like so:
//    00000
//    2
//    2b
//    14
//    A-01
//    A-3
//    A-13
//    bbb12342bfb333b-1-2-3-011
//    bbb12342bfb333b-1-2-21-01
// You may ask:  Why do we need a 'reverse' flag when we can just swap values in a calling function?
// Because with a flag, we can return reversed results for values that are defined,
// while continuing to sort undefined/empty/null values to the bottom.
export function sortWithNumberParsing(a:string|number|undefined|null, b:string|number|undefined|null, reverse?:boolean):number {
  var result = 0;

  // Just in case some lunatic passes in non-string things
  if (a === undefined || a === null) {
    return 1
  } else if (b === undefined || b === null) {
    return -1
  }

  // If we got two numbers, compare them as numbers.
  if ((typeof a === "number") && (typeof b === "number")) {
    result = a - b;
    return reverse ? (0-result) : result;
  }

  // Convert numbers to strings for consistent processing
  if (typeof a === "number") {
    a = a.toString();
  }
  if (typeof b === "number") {
    b = b.toString();
  }

  // A empty string gets moved to the bottom
  if (a.length < 1) {
    return 1
  } else if (b.length < 1) {
    return -1
  }

  var aIndex = 0;
  var bIndex = 0;

  var aNumberString:string[] = [];
  var bNumberString:string[] = [];

  while (!result) {
    var aChar = a.charAt(aIndex);
    aIndex++;
    // Seek further forward in the string for as long as we keep seeing digits.
    while ("0" <= aChar && aChar <= "9") {
        aNumberString.push(aChar);
        if (aIndex < a.length) {
          aChar = a.charAt(aIndex);
          aIndex++;
        } else {
          // Exit this loop. The contents of aChar are stale,
          // but also irrelevant now that we have a number.
          break;
        }
    }

    // Same procedure for string b.
    var bChar = b.charAt(bIndex);
    bIndex++;
    while ("0" <= bChar && bChar <= "9") {
        bNumberString.push(bChar);
        if (bIndex < b.length) {
          bChar = b.charAt(bIndex);
          bIndex++;
        } else {
          break;
        }
    }

    // If we got two numbers, compare them as numbers.
    if (aNumberString.length > 0 && aNumberString.length > 0) {
      const aNum = parseInt(aNumberString.join(""), 10);
      const bNum = parseInt(bNumberString.join(""), 10);
      result = aNum - bNum;
      // If the numbers are identical, result will be zero, and we'll need to keep going.
      // Clear the number arrays for the next iteration.
      aNumberString = [];
      bNumberString = [];

    // If the a segment is a number and the b segment is not, don't bother
    // parsing the number, just give it priority.
    } else if (aNumberString.length > 0) {
      result = -1;
    // Same in the other direction.
    } else if (bNumberString.length > 0) {
      result = 1;

    // At this point we know we got two non-number characters,
    // and have moved the pointers on to the next character, or off the end of the string,
    // for a and b.

    // If the two characters are not identical, use localeCompare to get a result.
    } else if (aChar !== bChar) {
      result = aChar.localeCompare(bChar);
    }

    // The remaining case of the the two characters being identical does not give us a result.

    // If we didn't get a result so far, check to see if we've moved off the end
    // of one string or the other, in which case we should favor the shorter string.
    if (result == 0) {
      if (aIndex >= a.length) {
        result = -1
      } else if (bIndex >= b.length) {
        result = 1
      }
    }
  }
  return reverse ? (0-result) : result;
}


// Given a target string, and a list of search strings,
// modify the target string so that all the occurrences of the search strings
// (searching case-insensitively) are boldfaced, and return the result as a ReactElement list.
// If no search strings occur in the target string, return a ReactElement list containing
// the target string as-is.
export function highlightSearchTermsInString(targetStr:string, searchTerms:string[]) : React.JSX.Element[] {

  interface StringRegion {
    start: number;
    end: number;
  }
  
  // If the string is empty, don't bother annotating.
  if (targetStr.length < 1) {
      return [<></>];
  }

  // If we have no search terms, don't annotate.
  if (searchTerms.length == 0) {
      return [<>{targetStr}</>];
  }

  const nonEmptySearchTerms =
    searchTerms.map((t) => t.trim()).filter((t) => t.length > 1).map((t) => t.toLocaleUpperCase());

  // If we have no non-blank search terms, don't annotate.
  if (nonEmptySearchTerms.length == 0) {
    return [<>{targetStr}</>];
  }

  // Test against the string in a case-insensitive manner
  const upperStr = targetStr.toLocaleUpperCase();

  // Accumulate a list of regions within the string that match a search term.

  function recursiveRegionFind(searchTerm: string, regions: StringRegion[], index: number): StringRegion[] {
    if (index >= targetStr.length) { return regions; }
    const matchValue = upperStr.indexOf(searchTerm, index) | 0;
    if (matchValue < 0) { return regions; }
    regions.push({ start: matchValue, end: (matchValue + searchTerm.length) });
    return recursiveRegionFind(searchTerm, regions, matchValue+1);
  }

  var regions:StringRegion[] = [];

  nonEmptySearchTerms.forEach((term) => {
    const firstFoundIndex = upperStr.indexOf(term.toLocaleUpperCase()) | 0;
    if (firstFoundIndex < 0) { return; }
    const startingList = [{start: firstFoundIndex, end: firstFoundIndex + term.length}];
    const completeList = recursiveRegionFind(term, startingList, firstFoundIndex + 1);
    regions = regions.concat(completeList);
  });

  // No matches on the search terms?  Nothing to highlight.
  if (regions.length == 0) {
    return [<>{targetStr}</>];
  }

  const sortedRegions = regions.sort((a, b) => (a.start - b.start));

  // Given an array of regions sorted by starting point, return a reduced set:
  // Every region that overlaps with another will be combined.
  function rubberBandReduce(reducedList: StringRegion[], currentMin: number, currentMax: number, remainingRegions: StringRegion[]): StringRegion[] {
    if (remainingRegions.length == 0) {
      reducedList.push({start: currentMin, end: currentMax});
      return reducedList;
    }
    const nextRegion = remainingRegions.shift();
    if (nextRegion!.start < currentMax + 1) {
      return rubberBandReduce(reducedList, currentMin, nextRegion!.end, remainingRegions);
    } else {
      reducedList.push({start: currentMin, end: currentMax});
      return rubberBandReduce(reducedList, nextRegion!.start, nextRegion!.end, remainingRegions);
    }
  }

  var reducedRegions = sortedRegions;
  if (reducedRegions.length > 1) {
    const firstRegion = reducedRegions.shift();
    reducedRegions = rubberBandReduce([], firstRegion!.start, firstRegion!.end, reducedRegions);
  }

  var highlightedString: React.JSX.Element[] = [];
  var lastRegionEnd: number = 0;

  while (reducedRegions.length > 0) {
    const thisRegion = reducedRegions.shift();
    
    const nonBoldStr = targetStr.slice(lastRegionEnd, thisRegion!.start);
    const boldStr = targetStr.slice(thisRegion!.start, thisRegion!.end);

    highlightedString.push(<>{nonBoldStr}</>);
    highlightedString.push(<b>{boldStr}</b>);
    
    lastRegionEnd = thisRegion!.end;
  }

  if (lastRegionEnd < targetStr.length) {
    const nonBoldStr = targetStr.slice(lastRegionEnd, targetStr.length);
    highlightedString.push(<>{nonBoldStr}</>);
  }

  return highlightedString;
}


// Given a suggested name (and potentially an index to start with),
// split the name into a prefix and a suffix, where the suffix is a number,
// and increment that number (or the given start index) to make a new name,
// until the new name is unique with respect to the given array of existing names.
// E.g. a given name of "abc-10-123" would render a new name of "abc-10-124",
// unless a name "abc-10-124" already exists, in which case the
// new name will be "abc-10-125", and so on.
// Or, if a startIndex of "2" is provided, a given name of "abc-10-123"
// would first try "abc-10-2", and proceed upwards from there.
export function generateUniqueNames(existingNames: string[], suggestedName: string, quantity?: number, startIndex?: number | null) {
  var chosenQuantity = Math.max(quantity||1, 1);
  var chosenStartIndex = startIndex || 1;

  const findTrailingNumber = /-([0-9]+)$/;
  // If any of the existing names look like this one with a number tacked on the end,
  // use the highest number as the chosen start index.
  existingNames.forEach((n) => {
    const match = findTrailingNumber.exec(n);
    if (match) {
      const thisPrefix = n.slice(0, n.length - match[0].length);
      if (thisPrefix == suggestedName) {
        const detectedIndex = parseInt(match[0], 10);
        chosenStartIndex = Math.max(chosenStartIndex, detectedIndex+1);
      }
    }
  });

  // If we need to optimize, caching this set somewhere would be
  // a good start.  Currently it's tricky because undo/redo
  // state can invalidate it, and we modify the set below.
  let existingNameSet: Set<string> = new Set();
  existingNames.forEach((v) => { existingNameSet.add(v) });

  var goodNames = [];

  // This weird sequence causes us to go with the suggested name, with no suffix, only if
  // we are generating a single name and the suggested name is unique.
  // If that name is not unique, we add a suffix.
  // If we are generating more than one name, we always add a suffix
  // (starting with "-1" if no other index is suggested or required.)
  var goodName = suggestedName;
  if (chosenQuantity > 1) {
      goodName = suggestedName + '-' + chosenStartIndex.toString();
  }
  while (chosenQuantity > 0) {
    while (existingNameSet.has(goodName)) {
      goodName = suggestedName + '-' + chosenStartIndex.toString();
      chosenStartIndex++;
    }
    goodNames.push(goodName);
    existingNameSet.add(goodName);
    chosenQuantity--;
  }
  return goodNames;
}



// -1 : Print as a full float
//  0 : Print as an int, ALWAYS rounded down.
// +n : Print with n decimal places, UNLESS the value is an integer
export function nicelyPrintFloat(v: number, places: number): string {
  // We do not want to display ANY decimal point if the value is an integer.
  if (v % 1 === 0) {	// Basic integer test
    return (v % 1).toString();
  }
  if (places > 0) {
    return v.toFixed(places);
  } else if (places == 0) {
    return (v % 1).toString();
  }
  return v.toString();
}


// Convert a size provided in bytes to a nicely formatted string
export function sizeToString(size: number): string {

  var tb = size / (1024 * 1024 * 1024 * 1024);
  if ((tb > 1) || (tb < -1)) {
    return nicelyPrintFloat(tb, 2) + ' Tb';
  }
  var gigs = size / (1024 * 1024 * 1024);
  if ((gigs > 1) || (gigs < -1)) {
    return nicelyPrintFloat(gigs, 2) + ' Gb';
  }
  var megs = size / (1024 * 1024);
  if ((megs > 1) || (megs < -1)) {
    return nicelyPrintFloat(megs, 2) + ' Mb';
  }
  var k = size / 1024;
  return nicelyPrintFloat(k, 2) + ' Kb';
}


// Given a POSIX timestamp in seconds (with a possible fractional portion being milliseconds),
// based on zero being midnight of Jan 1, 1970 (standard POSIX time),
// return a string formatted in the manner of "Dec 21 2012, 11:45am",
// with exceptions for 'Today' and 'Yesterday', e.g. "Yesterday, 3:12pm".
export function timestampToTodayString(timestamp: number): string {

  // Code adapted from Perl's HTTP-Date
  //var DoW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  var MoY = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  if (!timestamp || timestamp < 1) {
    return 'N/A';
  }

  var t = new Date(Math.round(timestamp*1000));
  var n = new Date();
  var now = n.getTime();

  var sec = t.getSeconds();
  var min = t.getMinutes();
  var hour = t.getHours();
  var mday = t.getDate();		// Returns the day of the month (from 1-31)
  var mon = t.getMonth();		// Returns the month (from 0-11)
  var year = t.getFullYear();	// Returns the year (four digits)
  var wday = t.getDay();		// Returns the day of the week (from 0-6)

  var nsec = n.getSeconds();
  var nmin = n.getMinutes();
  var nhour = n.getHours();
  var nmday = n.getDate();
  var nmon = n.getMonth();
  var nyear = n.getFullYear();

  var dayStr;

  if ((year == nyear) && (mon == nmon) && (mday == nmday)) {
    dayStr = 'Today';
  } else if (	    (now - (nsec + (60*(nmin+(60*(nhour+24)))))) ==		// Now's day component minus a day
            (timestamp - (sec  + (60*(min +(60* hour     ))))))	 {	// Timestamp's day component
    dayStr = 'Yesterday';
  } else {
    var year_str = '';
    if (year != nyear) {
      year_str = ' ' + year;
    }
    dayStr = MoY[mon] + ' ' + mday + year_str;
  }

  var halfDay = (hour > 11) ? 'pm' : 'am';
  if (hour > 12) {hour -= 12;}
  else if (hour == 0) {hour = 12;}
  const minStr = min.toString().padStart(2, '0')

  return dayStr + ', ' + hour + ':' + minStr + halfDay;
}


export function generateDistinctHSLColors(count: number): string[] {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const hue = Math.round((360 / count) * i);
    colors.push(`hsl(${hue}, 95%, 20%)`);
  }
  return colors;
}
